import React, { useEffect, useState, useRef, Component } from 'react';
/**
 * MoneyAssembler — Interactive coin/bill selection for money questions.
 *
 * DDD co-locatie: moved from components/practice/ in s65.
 * Optimized: style definitions extracted to moneyStyles.ts (~140r).
 */
import { motion, AnimatePresence } from 'framer-motion';
import type { Question, MoneyDenom } from '../../content/types';
import { X, Wallet, RotateCcw } from 'lucide-react';
import { error as logError } from '../../utils/logger';
import { getMoneyStyles, isBill, getDenominationDisplay } from './moneyStyles';
// ── Types ────────────────────────────────────────────────────
interface MoneyAssemblerProps {
  question: Question;
  onComplete: (userAnswer: string) => void;
  onReadyChange?: (isReady: boolean) => void;
  shouldValidate?: boolean;
}
// ── MoneyItem Sub-component ──────────────────────────────────
function MoneyItem({
  cents,
  allowedDenoms,
  onClick,
  disabled = false,
  small = false,
  isFocused = false
}: {
  cents: number;
  allowedDenoms: MoneyDenom[];
  onClick?: () => void;
  disabled?: boolean;
  small?: boolean;
  isFocused?: boolean;
}) {
  const styles = getMoneyStyles(cents);
  const isBillItem = isBill(cents, allowedDenoms);
  const scaleClass = small ? 'scale-75 origin-center' : '';
  return <motion.button whileHover={!disabled && !small ? {
    scale: 1.08,
    rotate: isBillItem ? 0 : 2,
    y: -3
  } : {}} whileTap={!disabled && !small ? {
    scale: 0.92
  } : {}} onClick={onClick} disabled={disabled} className={`
        relative flex items-center justify-center
        ${styles.container} ${styles.bg} ${styles.border}
        ${scaleClass}
        transition-all duration-200
        ${!disabled && !small ? 'hover:shadow-2xl cursor-pointer' : ''}
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        ${isFocused ? 'ring-4 ring-indigo-400 ring-offset-2' : ''}
      `} aria-label={getDenominationDisplay(cents, allowedDenoms)} data-id="element-7680">
      <div className={`${styles.inner} relative z-10`} data-id="element-7681">
        <span className={`${styles.text} drop-shadow-md`} data-id="element-7682">
          {getDenominationDisplay(cents, allowedDenoms)}
          {cents < 100 && <span className="text-[0.65em] ml-0.5" data-id="element-7683">c</span>}
        </span>
      </div>
      {isBillItem && <div className="absolute right-3 top-0 bottom-0 w-2 bg-white/15 border-l border-r border-white/25 rounded-r-lg" data-id="element-7684" />}
    </motion.button>;
}
// ── Main Component ───────────────────────────────────────────
export function MoneyAssembler({
  question,
  onComplete,
  onReadyChange,
  shouldValidate = false
}: MoneyAssemblerProps) {
  // CANONICAL: Read ONLY from data.money (no legacy fallback)
  const moneyConfig = question.data?.money;
  const allowedDenoms: MoneyDenom[] = moneyConfig?.allowedDenoms || [];
  const availableDenominations = allowedDenoms.map(d => d.valueCents);
  const [selectedCoins, setSelectedCoins] = useState<number[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const prevValidateRef = useRef(false);
  const isReady = selectedCoins.length > 0;
  useEffect(() => {
    onReadyChange?.(isReady);
  }, [isReady, onReadyChange]);
  // Reset validation flag when shouldValidate becomes false
  useEffect(() => {
    if (!shouldValidate) {
      prevValidateRef.current = false;
    }
  }, [shouldValidate]);
  // Rising-edge detection for shouldValidate (consistent with all other renderers)
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      const countMap = new Map<number, number>();
      selectedCoins.forEach(cents => {
        countMap.set(cents, (countMap.get(cents) || 0) + 1);
      });
      const tokens = Array.from(countMap.entries()).filter(([_, count]) => count > 0).map(([valueCents, count]) => ({
        valueCents,
        count
      }));
      onComplete(JSON.stringify(tokens));
    }
  }, [shouldValidate, selectedCoins, onComplete]);
  // Reset when question changes
  useEffect(() => {
    setSelectedCoins([]);
    setFocusedIndex(0);
    prevValidateRef.current = false;
  }, [question.id]);
  // Keyboard support
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setFocusedIndex(prev => Math.max(0, prev - 1));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setFocusedIndex(prev => Math.min(availableDenominations.length - 1, prev + 1));
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setSelectedCoins(prev => [...prev, availableDenominations[focusedIndex]]);
      } else if (e.key === 'Backspace' && selectedCoins.length > 0) {
        e.preventDefault();
        setSelectedCoins(prev => prev.slice(0, -1));
      } else if (e.key === 'Escape' && selectedCoins.length > 0) {
        e.preventDefault();
        setSelectedCoins([]);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [availableDenominations, focusedIndex, selectedCoins]);
  // Guard: missing config — rendered AFTER all hooks
  if (!moneyConfig || !moneyConfig.allowedDenoms) {
    logError('MoneyAssembler: Missing money config in question.data.money');
    return <div className="text-center p-8 bg-red-50 rounded-xl border-2 border-red-200" data-id="element-7685">
        <p className="text-red-700 font-bold" data-id="element-7686">⚠️ Geld configuratie ontbreekt</p>
        <p className="text-sm text-red-600 mt-2" data-id="element-7687">
          Deze vraag heeft geen geldige geld data.
        </p>
      </div>;
  }
  return <div className="w-full max-w-3xl mx-auto space-y-6" data-id="element-7688">
      {/* Keyboard hints - only on desktop */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-center hidden md:block" data-id="element-7689">
        <p className="text-xs text-indigo-700 font-medium" data-id="element-7690">
          💡 <strong data-id="element-7691">Toetsenbord:</strong> ← → om te navigeren • Enter/Spatie om
          te selecteren • Backspace om laatste te verwijderen • Esc om te
          resetten
        </p>
      </div>

      {/* Step 1: Selection */}
      <div className="space-y-3" data-id="element-7692">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2" data-id="element-7693">
          <span className="w-5 h-5 rounded-full bg-indigo-500 text-white flex items-center justify-center text-[10px]" data-id="element-7694">
            1
          </span>
          Kies munten en biljetten
        </h3>

        <div className="bg-white rounded-2xl p-4 md:p-6 border border-slate-200 shadow-sm" data-id="element-7695">
          <div className="flex flex-wrap gap-3 md:gap-4 justify-center items-center" data-id="element-7696">
            {availableDenominations.map((denomination: number, index: number) => <motion.div key={`denom-${denomination}`} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.03
          }} data-id="element-7697">
                  <MoneyItem cents={denomination} allowedDenoms={allowedDenoms} onClick={() => setSelectedCoins(prev => [...prev, denomination])} isFocused={index === focusedIndex} data-id="element-7698" />
                </motion.div>)}
          </div>
        </div>
      </div>

      {/* Step 2: Wallet */}
      <div className="space-y-3" data-id="element-7699">
        <div className="flex items-center justify-between" data-id="element-7700">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2" data-id="element-7701">
            <span className="w-5 h-5 rounded-full bg-indigo-500 text-white flex items-center justify-center text-[10px]" data-id="element-7702">
              2
            </span>
            Jouw portemonnee
          </h3>

          {selectedCoins.length > 0 && <motion.button initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} onClick={() => setSelectedCoins([])} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-full transition-colors" data-id="element-7703">
              <RotateCcw className="w-3.5 h-3.5" data-id="element-7704" />
              Opnieuw
            </motion.button>}
        </div>

        <div className="relative min-h-[140px] bg-slate-50 rounded-2xl border-2 border-dashed border-slate-300 overflow-hidden" data-id="element-7705">
          {selectedCoins.length === 0 && <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 pointer-events-none" data-id="element-7706">
              <Wallet className="w-10 h-10 mb-2 opacity-30" data-id="element-7707" />
              <p className="text-sm font-medium" data-id="element-7708">De portemonnee is nog leeg</p>
            </div>}

          <div className="relative z-10 p-4 flex flex-wrap content-start gap-2" data-id="element-7709">
            <AnimatePresence mode="popLayout" data-id="element-7710">
              {selectedCoins.map((coin, index) => <motion.div key={`selected-${index}-${coin}`} layout initial={{
              scale: 0,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} exit={{
              scale: 0,
              opacity: 0
            }} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} className="relative group/coin" data-id="element-7711">
                  <MoneyItem cents={coin} allowedDenoms={allowedDenoms} small data-id="element-7712" />
                  <button onClick={() => setSelectedCoins(prev => prev.filter((_, i) => i !== index))} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md opacity-100 md:opacity-0 md:group-hover/coin:opacity-100 transition-opacity" aria-label="Verwijder" data-id="element-7713">
                    <X className="w-3 h-3" data-id="element-7714" />
                  </button>
                </motion.div>)}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>;
}